<template>
    <div id="account">
        <section id="account-hero">
            <div class="hero-content">
                <h1>My Content</h1>
                <p v-if="userName">Welcome back, {{ userName }}</p>
            </div>
            <button class="sign-out shadow" @click="signOut">Sign out</button>
        </section>

        <section class="content-section fade-in">
            <h2>ReSeT for Education</h2>
            <p class="section-desc">Your course films. Click each title to watch.</p>
            <div class="modules">
                <div
                    class="module"
                    v-for="(module, index) in educationModules"
                    :key="index"
                >
                    <div class="module-header" @click="toggleModule(index)">
                        <div class="module-meta">
                            <span class="module-number">{{ index + 1 }}</span>
                            <h3>{{ module.title }}</h3>
                        </div>
                        <span class="toggle-icon">{{ activeModule === index ? '−' : '+' }}</span>
                    </div>
                    <div class="module-body" v-if="activeModule === index">
                        <video
                            v-if="module.videoUrl"
                            :src="module.videoUrl"
                            controls
                            preload="metadata"
                        ></video>
                        <p v-else-if="module.loading" class="coming-soon">Loading film…</p>
                        <p v-else-if="module.error" class="coming-soon">
                            This film isn't available yet. Please check back soon.
                        </p>
                        <p v-else class="coming-soon">Film coming soon</p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { getAuth, signOut } from "firebase/auth";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { firebaseStorage } from "../firebase";

// Films live in Firebase Storage under videos/education/ and are readable only
// by signed-in users (see storage.rules). URLs are resolved through the SDK on
// demand so no download tokens are baked into the built bundle.
const EDUCATION_FILMS = [
    { title: "Introduction: Jonny and Jeanie's Story, Part 1", file: "01-reset-intro-jonny-jeanie-part-1.mp4" },
    { title: "Introduction: Jonny and Jeanie's Story, Part 2", file: "02-reset-intro-jonny-jeanie-part-2.mp4" },
    { title: "The Restorative Engagement Forum", file: "03-restorative-engagement-forum-intro.mp4" },
    { title: "The 11th and 12th Centuries", file: "04-11th-and-12th-century.mp4" },
    { title: "Parent, Adult, Child: David's Story", file: "05-parent-adult-child-davids-story.mp4" },
    { title: "Parent, Adult, Teacher: The Crisps Story", file: "06-parent-adult-teacher-crisps-story.mp4" },
    { title: "Parent, Adult, Child and the Drama Triangle", file: "07-parent-adult-child-drama-triangle.mp4" },
    { title: "The Drama Triangle, Part 2", file: "08-drama-triangle-part-2.mp4" },
    { title: "The Drama Triangle: A Reminder", file: "09-drama-triangle-reminder.mp4" },
    { title: "The Compass of Shame", file: "10-compass-of-shame.mp4" },
    { title: "I-Messaging", file: "11-i-messaging.mp4" },
];

export default {
    data() {
        return {
            userName: "",
            activeModule: null,
            educationModules: EDUCATION_FILMS.map((film) => ({
                ...film,
                videoUrl: "",
                loading: false,
                error: false,
            })),
        };
    },
    props: ["fadeIn"],
    mounted() {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
            this.userName = user.displayName || user.email;
        }
        this.fadeIn();
        setTimeout(() => {
            ScrollTrigger.refresh();
        }, 1000);
    },
    methods: {
        toggleModule(index) {
            this.activeModule = this.activeModule === index ? null : index;
            if (this.activeModule === index) {
                this.loadFilm(index);
            }
        },
        async loadFilm(index) {
            const module = this.educationModules[index];
            if (module.videoUrl || module.loading) return;

            module.loading = true;
            module.error = false;
            try {
                module.videoUrl = await getDownloadURL(
                    storageRef(firebaseStorage, `videos/education/${module.file}`)
                );
            } catch (err) {
                module.error = true;
            } finally {
                module.loading = false;
            }
        },
        async signOut() {
            const auth = getAuth();
            await signOut(auth);
            this.$router.push("/signin");
        },
    },
};
</script>

<style lang="scss" scoped>
#account {
    min-height: 100vh;
}

#account-hero {
    @include for-phone-only() {
        flex-direction: column;
        align-items: flex-start;
        gap: 1em;
        padding: 2em 1em;
    }
    background: $blue;
    padding: 3em 8%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .hero-content {
        h1 {
            color: #fff;
            margin: 0 0 0.25em;
        }
        p {
            color: rgba(255, 255, 255, 0.7);
            margin: 0;
        }
    }

    .sign-out {
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.4);
        color: #fff;
        padding: 0.6em 1.5em;
        border-radius: 25px;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.2s;

        &:hover {
            background: rgba(255, 255, 255, 0.1);
        }
    }
}

.content-section {
    @include for-phone-only() {
        padding: 2em 1em;
    }
    padding: 4em 8%;

    h2 {
        color: $blue;
        margin: 0 0 0.5em;
    }

    .section-desc {
        color: #666;
        margin: 0 0 2em;
    }
}

.modules {
    display: flex;
    flex-direction: column;
    gap: 1em;
}

.module {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;

    .module-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.2em 1.5em;
        cursor: pointer;
        background: #fff;
        transition: background 0.2s;

        &:hover {
            background: #f9f9f9;
        }

        .module-meta {
            display: flex;
            align-items: center;
            gap: 1em;

            .module-number {
                background: $blue;
                color: #fff;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.85em;
                flex-shrink: 0;
            }

            h3 {
                margin: 0;
                color: $blue;
                font-size: 1em;
            }
        }

        .toggle-icon {
            color: $blue;
            font-size: 1.5em;
            line-height: 1;
        }
    }

    .module-body {
        background: #000;
        padding: 0;

        video {
            width: 100%;
            max-height: 500px;
            display: block;
        }

        .coming-soon {
            color: #fff;
            text-align: center;
            padding: 3em;
            margin: 0;
            opacity: 0.5;
        }
    }
}
</style>
